/**
 * @fileoverview Analytics tracker utilities built around Amplitude and
 * Segment integration hooks.
 *
 * @module lib/Tracker
 */

import amplitude from 'amplitude-js';
import { segmentIdentifyAndTrackPage } from './segment';

/**
 * Safely executes a function, returning null on any error.
 *
 * @param {Function} func - Function to execute
 * @returns {*|null} Function result or null if error occurred
 */
function safeCall(func) {
  try {
    return func();
  } catch {
    return null;
  }
}

/**
 * Initializes the Amplitude SDK with the configured API key.
 * Should be called once on app startup.
 */
function init() {
  amplitude.getInstance().init(process.env.REACT_APP_AMPLITUDE_API_KEY);
}

/**
 * Analytics tracker that wraps Amplitude and Google Tag Manager.
 *
 * Provides methods for tracking events, setting user properties, and
 * managing UTM parameters. Events are sent to both Amplitude and
 * Google Tag Manager's dataLayer.
 */
class Tracker {
  /**
   * Creates a new Tracker instance.
   *
   * @param {Object} params
   * @param {Object} params.auth - Auth context for accessing user state
   */
  constructor(params) {
    this.auth = params.auth;
    this.globalProperties = {};
  }

  /**
   * Sets properties that will be included with every tracked event.
   *
   * @param {Object} properties - Key-value pairs to include in all events
   */
  setGlobalProperties(properties) {
    for (const key of Object.keys(properties)) {
      this.globalProperties[key] = properties[key];
    }
  }

  /**
   * Sets the Amplitude device ID for cross-device tracking.
   *
   * @param {string} installationToken - Unique device/installation identifier
   */
  setAmplitudeUserDevice = (installationToken) => {
    amplitude.getInstance().setDeviceId(installationToken);
  };

  /**
   * Sets the Amplitude user ID for user-level analytics.
   *
   * @param {string} userId - User's unique identifier
   */
  setUserId = (userId) => {
    amplitude.getInstance().setUserId(userId);
  };

  /**
   * This will set user properties for any utm_ parameters in the url and return those properties
   *  to be put in event properties
   */
  setUtmParameters() {
    const utmProperties = {};
    if (window.location.href) {
      const utmMatches = window.location.href.match(/utm_(medium|source|campaign|content)=([^&]*)/g) || [];
      if (utmMatches.length > 0) {
        for (const utmMatch of utmMatches) {
          const [key, value] = utmMatch.split('=');
          utmProperties[key] = value;
        }
        this.setUserProperties(utmProperties);
      }
    }
    return utmProperties;
  }

  /**
   * Sets persistent user properties in Amplitude.
   *
   * @param {Object} properties - User properties to set (e.g., plan type, signup date)
   */
  setUserProperties = (properties) => {
    amplitude.getInstance().setUserProperties(properties);
  };

  /**
   * Logs an event to Amplitude and Google Tag Manager.
   *
   * Automatically includes:
   * - Global properties set via setGlobalProperties
   * - Common properties (auth state, qualified state)
   * - UTM parameters from the URL
   *
   * @param {string} eventType - Name of the event (e.g., 'LETTER_ADOPTED', 'PAGE_VIEW')
   * @param {Object} [eventProperties] - Additional properties specific to this event
   */
  logEvent = (eventType, eventProperties) => {
    try {
      amplitude.getInstance().logEvent(
        eventType, {
          ...eventProperties,
          ...this.commonProperties(),
          ...this.globalProperties,
          ...this.setUtmParameters(),
        },
      );
      if (window?.dataLayer) {
        window.dataLayer.push({
          event: eventType,
          eventProps: {
            ...this.globalProperties,
            ...this.commonProperties(),
            ...this.setUtmParameters(),
            ...eventProperties,
          },
        });
      }
    } catch {
      //do nothing
    }
  };

  /**
   * Sends user identification and page view to Segment.
   */
  trackSegment = () => {
    segmentIdentifyAndTrackPage(this.auth.getUserId());
  };

  /**
   * Returns common properties to include with every event.
   *
   * Includes authentication and qualification state from the auth context.
   *
   * @returns {Object} Common event properties
   */
  commonProperties = () => {
    const properties = {};
    if (this.auth) {
      properties.isAuthenticated = safeCall(this.auth.isAuthenticated);
      properties.isQualified = safeCall(this.auth.isQualified);
      properties.isEmailVerified = safeCall(this.auth.isEmailVerified);
      properties.localQualifiedState = localStorage.getItem('user_qualified_state');
    }
    return properties;
  };
}

export default Tracker;
export { init };
